import {COVER_TYPES} from '../constants/renewalClick';

const formatCurrency = (amount) => `₪${Number(amount).toLocaleString('he-IL')}`;

function premiumLabelId(coverType) {
  return coverType === COVER_TYPES.THIRD_PARTY
    ? 'renewalClick.details.thirdPartyPremium'
    : 'renewalClick.details.cascoPremium';
}

// Every priced item shown in the offer card breakdown gets its own row here too,
// not just mandatory/casco.
function buildPricingRows(pricing, policy, m) {
  return [
    {label: m('renewalClick.details.mandatoryPremium'), value: formatCurrency(pricing.mandatoryPremium)},
    {
      label: m(premiumLabelId(policy.coverType)),
      value: formatCurrency(pricing.cascoPremium),
    },
    {
      label: m('renewalClick.details.serviceFee'),
      value: pricing.serviceFee > 0 ? formatCurrency(pricing.serviceFee) : undefined,
    },
    ...(pricing.otherProducts || []).map((product) => ({
      label: product.name,
      value: formatCurrency(product.amount),
    })),
    {label: m('renewalClick.details.total'), value: formatCurrency(pricing.totalPremium)},
  ];
}

/**
 * Maps the structured page model into display-ready policy detail rows.
 * @param {{customer, vehicle, policy, pricing}} model
 * @param {(id: string) => string} m - intl.formatMessage shorthand
 * @returns {{label: string, value: string}[]}
 */
export function buildPolicyDetailRows({customer, vehicle, policy, pricing}, m) {
  // Insurance_Type_Xyears_Ago__c is a picklist ("Comprehensive"/"Third Party"/
  // "Without"); blank/unrecognized values are unknown, so the row is omitted.
  const INSURANCE_TYPE_LABELS = {
    Comprehensive: m('renewalClick.details.comprehensive'),
    'Third Party': m('renewalClick.details.thirdParty'),
    Without: m('renewalClick.details.withoutInsurance'),
  };
  function insuranceLabel(val) {
    return INSURANCE_TYPE_LABELS[val];
  }
  // Claim_*/Body_Claim_* hold a claim count string ("0","1","2"...): 0 means
  // "no claims", null/undefined means unknown and the row is omitted.
  function claimLabel(val) {
    if (val === null || val === undefined || val === '') return undefined;
    return Number(val) === 0 ? m('renewalClick.details.noClaims') : String(val);
  }
  function formatDate(d) {
    return d ? new Date(d).toLocaleDateString('he-IL') : '';
  }

  return [
    {label: m('renewalClick.details.insuranceType'), value: policy.type},
    {label: m('renewalClick.details.agency'), value: policy.agencyName},
    {label: m('renewalClick.details.insuredName'), value: customer.name},
    {label: m('renewalClick.details.manufacturer'), value: vehicle.manufacturer},
    {label: m('renewalClick.details.vehicleYear'), value: String(vehicle.year || '')},
    {label: m('renewalClick.details.vehicleModel'), value: vehicle.subModel},
    {label: m('renewalClick.details.modelCode'), value: vehicle.modelCode},
    {label: m('renewalClick.details.insuranceCompany'), value: policy.insuranceCompany},
    {label: m('renewalClick.details.insurancePlan'), value: policy.insurancePlan},
    {label: m('renewalClick.details.policyPeriod'), value: `${formatDate(policy.endDate)} - ${formatDate(policy.startDate)}`},
    {label: m('renewalClick.details.policyOwner'), value: customer.name},
    {label: m('renewalClick.details.identification'), value: customer.identification},
    {label: m('renewalClick.details.authorizedDrivers'), value: customer.authorizedDrivers},
    {label: m('renewalClick.details.youngestDriverAge'), value: String(customer.age || '')},
    {label: m('renewalClick.details.licensePlate'), value: vehicle.licensePlate?.replace(/-/g, '')},
    {label: m('renewalClick.details.email'), value: customer.email},
    {label: m('renewalClick.details.driverSeniority'), value: policy.driverSeniority},
    {label: m('renewalClick.details.insuranceLastYear'), value: insuranceLabel(policy.insuranceHistory?.lastYear)},
    {label: m('renewalClick.details.insuranceTwoYears'), value: insuranceLabel(policy.insuranceHistory?.twoYearsAgo)},
    {label: m('renewalClick.details.insuranceThreeYears'), value: insuranceLabel(policy.insuranceHistory?.threeYearsAgo)},
    {label: m('renewalClick.details.propertyClaimsLastYear'), value: claimLabel(policy.claimsHistory?.property?.lastYear)},
    {label: m('renewalClick.details.propertyClaimsTwoYears'), value: claimLabel(policy.claimsHistory?.property?.twoYearsAgo)},
    {label: m('renewalClick.details.propertyClaimsThreeYears'), value: claimLabel(policy.claimsHistory?.property?.threeYearsAgo)},
    {label: m('renewalClick.details.bodilyClaimsLastYear'), value: claimLabel(policy.claimsHistory?.bodily?.lastYear)},
    {label: m('renewalClick.details.bodilyClaimsTwoYears'), value: claimLabel(policy.claimsHistory?.bodily?.twoYearsAgo)},
    {label: m('renewalClick.details.bodilyClaimsThreeYears'), value: claimLabel(policy.claimsHistory?.bodily?.threeYearsAgo)},
    {
      label: m('renewalClick.details.licenseSuspension'),
      value: policy.licenseSuspension ? m('renewalClick.details.yes') : m('renewalClick.details.noSuspension'),
    },
    {label: m('renewalClick.details.roadServicesCompany'), value: policy.roadServicesCompany},
    {
      label: m('renewalClick.details.windowsCoverage'),
      value: policy.windowsCoverage ? m('renewalClick.details.included') : undefined,
    },
    ...buildPricingRows(pricing, policy, m),
  ].filter((row) => row.value);
}

